import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { QfaEventType } from '../enums/event-type.enum';
import { LoggingService } from './logging.service';

/* eslint-disable @typescript-eslint/no-explicit-any */
@Injectable({ providedIn: 'root' })
export class AuthService {
  protected accessJWT: Object | null = null;
  protected idJWT: Object | null = null;
  private readonly router: Router = inject(Router);
  private readonly logging: LoggingService = inject(LoggingService);
  private isInternal: boolean = false;

  constructor(protected http: HttpClient) {}

  /**
   * Initiates the OAuth2 login flow or checks if a valid session exists.
   *
   * If a valid access token is present and not expired, the method returns true.
   * Otherwise, it triggers the OAuth2 PKCE flow and redirects the user to the login page.
   *
   * @returns {Promise<boolean>} A promise that resolves to true if a redirect was performed or a session exists, false otherwise.
   */
  async login(): Promise<boolean> {
    this.captureOriginalUrl();
    // Set loginUrl conditionally
    const baseOAuthUrl = this.isInternal ? environment.oAuthBaseUrl : environment.oAuthBaseUrlIFA;
    const authConfigClone = { ...authConfig };
    authConfigClone.loginUrl = baseOAuthUrl + '/services/oauth2/authorize';

    // Check if we have an existing active Sales force access token and if we can refresh our existing session with it
    if (sessionStorage.getItem('sf_access_token') !== null) {
      const continueSession = await this._persistSession();
      // return if we can stay logged in or if we should logout, clear local storage and start anew
      return continueSession;
    }

    // Otherwise navigate to login page
    this.logging.logEvent(QfaEventType.INFO, 'Prompting new login.');
    // 1. Grab items from PKCE to build login url
    const { code_challenge_method, code_challenge, code_verifier } = await this.generatePKCE();

    if (code_challenge_method !== '' && code_challenge !== '' && code_verifier !== '') {
      sessionStorage.setItem('sf_code_verifier', code_verifier);
      sessionStorage.setItem('sf_code_challenge_method', code_challenge_method);
      const loginPageUrl = new URL(authConfigClone.loginUrl);

      const params = loginPageUrl.searchParams;
      params.set('redirect_uri', authConfig.redirectUri);
      params.set('client_id', authConfig.clientId);
      params.set('scope', authConfig.scope);
      params.set('code_challenge', code_challenge);
      params.set('code_challenge_method', code_challenge_method);
      params.set('response_type', 'code');
      // 2. Redirect to login url. Flow continues in method completeLoginOAuthCallback ()
      window.location.replace(loginPageUrl.href);
      this.logging.logEvent(QfaEventType.DEBUG, 'login page re-direct complete.');
    }

    return false;
  }

  /**
   * Called from call-back screen after salesforce redirects to it after login
   * Handles the OAuth2 authorization code callback process.
   *
   * Exchanges the authorization code in the URL for Sales Force access token to use as api call bearer token.
   * Stores the access_token in sessionStorage, fetches the current user's username,
   * and navigates the user to the originally requested URL.
   *
   * Retries the token exchange up to 5 times, attempting silent re-authentication if token exchange fails.
   * Logs authentication and error events throughout the process.
   *
   * @returns {Promise<boolean>} Promise that resolves to true if login completes successfully, false otherwise.
   */
  async completeLoginOAuthCallback(): Promise<boolean> {
    this.logging.logEvent(QfaEventType.DEBUG, 'Starting OAuth callback processing.');

    const maxRetries = 5;
    let attempt = 0;
    while (attempt < maxRetries) {
      attempt++;
      //continuing Auth steps from from login() method
      // 3. Given that we've tried logging in and have been redirected to /callback with special params, ask SaleForce for access token using param values
      const tokenResponse = await this.attemptTokenExchange();

      if (tokenResponse?.access_token) {
        // 4. Store access token in order to use it as bearer token in api requests
        const url = await this._storeSFObjectsLocally(tokenResponse);
        if (url != null) {
          this.router.navigate([url ?? '/']);
        }

        // Exit early on success
        return true;
      } else {
        // token not acquired, re-try login
        const reloginSuccess = await this.tryReauthenticate('Silent login failed.');
        if (!reloginSuccess && attempt < maxRetries) {
          // relogin failed, stop trying
          return false;
        }
      }
      // add a small delay to prevent fast looping
      await new Promise(res => setTimeout(res, 1000));
    }
    this.logging.logEvent(QfaEventType.WARNING, 'Max authentication retries reached without a valid token.');
    return false;
  }

  async tryReauthenticate(messageFromOrigin: string): Promise<boolean> {
    try {
      // kick off full login flow (this.login() will redirect)
      return await this.login();
    } catch {
      this.logging.logEvent(QfaEventType.WARNING, messageFromOrigin ?? 'Login failed.');
      return false;
    }
  }

  async logout(): Promise<void> {
    // SF JWT Tokens cannot be individually revoked. Clean up the clientside instead.
    const originalUrl = sessionStorage.getItem('originalUrl');
    this.clearTokens();
    sessionStorage.clear();
    if (originalUrl != null) {
      sessionStorage.setItem('originalUrl', originalUrl);
    }
    await this.login();
  }

  // ----- HELPER METHODS -----

  private async _persistSession(): Promise<boolean> {
    //check if token expired via trying to grab user details via existing token
    this.logging.logEvent(QfaEventType.DEBUG, 'AuthService.persistSession - checking user session');
    const username = await this.fetchUserInfo();
    if (!username) {
      //if token is expired, try to use refresh token
      const tokenResponse = await this.attemptTokenExchange(true);
      if (tokenResponse) {
        await this._storeSFObjectsLocally(tokenResponse);
        return true;
      } else {
        this.logging.logEvent(QfaEventType.WARNING, 'AuthService.persistSession - session expired');
        return false; //session and refresh tokens have expired, need ot login into new session
      }
    }
    return true;
  }

  /**
   * Generate PKCE (Proof Key of Code Exchange) values by calling SalesForce's online PKCE generator
   *
   * @remarks In terms of the quality of SalesForce's internal implementation
   * of RFC7636, this is suspicious in the _extreme_. If you generate PKCE according
   * to the RFC, the SF endpoint works only sporadically correctly. If you use this,
   * presto, it works everytime! No other OAuth provider supplies such an endpoint,
   * it is not documented in any of the standard flows, and the whole point of PKCE
   * is to _avoid_ the server (or man in the middle attack) knowing what this is. But anyway, it's the
   * only way it reliably works. So Caveat, Programmer. - Tom C, Jul 2025
   *
   * @returns SalesForcePKCEGeneratorResponse - the challenge, verifier and method values.
   */
  protected async generatePKCE(): Promise<SalesForcePKCEGeneratorResponse> {
    const nullPKCE = {
      code_challenge_method: '',
      code_challenge: '',
      code_verifier: '',
    };
    try {
      const data = await firstValueFrom(this.http.get<string>(authConfig.salesForcePKCEGeneratorUrl));
      return (data ?? nullPKCE) as any as SalesForcePKCEGeneratorResponse;
    } catch (error) {
      this.logging.logEvent(QfaEventType.WARNING, 'AuthService generatePKCE ERROR');
      this.logging.logError(error as Error);
      return nullPKCE;
    }
  }

  /**
   * Uses url params as tokens from callback to ask Sales force for access token
   *
   * @returns {Promise<OAuthAuthorizationResponse | null>} - Resolves with the OAuthAuthorizationResponse if successful, or null on failure.
   */
  private async attemptTokenExchange(isSilentRefresh: boolean = false): Promise<OAuthAuthorizationResponse | null> {
    const codeVerifier = sessionStorage.getItem('sf_code_verifier');
    const codeChallengeMethod = sessionStorage.getItem('sf_code_challenge_method');
    const refreshToken = sessionStorage.getItem('sf_refresh_token');
    const code = new URLSearchParams(document.location.search).get('code');

    let data = null;
    if (isSilentRefresh) {
      if (!refreshToken) {
        return null;
      }
      data = {
        client_id: authConfig.clientId,
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      };
    } else {
      if (!codeVerifier || !codeChallengeMethod || !code) {
        return null;
      }
      data = {
        client_id: authConfig.clientId,
        redirect_uri: authConfig.redirectUri,
        code,
        grant_type: 'authorization_code',
        code_verifier: codeVerifier,
        code_challenge_method: codeChallengeMethod,
      };
    }

    const dataStr = this.objectToXWWWFormUrlencoded(data);
    try {
      const response = await firstValueFrom(
        this.http.post<OAuthAuthorizationResponse>(authConfig.tokenEndpoint, dataStr, {
          headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
          responseType: 'json',
        }),
      );
      // Cleanup
      sessionStorage.removeItem('sf_code_verifier');
      sessionStorage.removeItem('sf_code_challenge_method');
      return response;
    } catch (error) {
      this.logging.logEvent(QfaEventType.WARNING, 'AuthService tokenExchange ERROR');
      this.logging.logError(error as Error);
      return null;
    }
  }

  /**
   * Helper method from from attemptTokenExchange() called from call-back screen after salesforce redirects to it after login
   * Fetches the current user's information from the OAuth2 userinfo endpoint.
   *
   * @returns {Promise<string | null>} A promise that resolves to the user's preferred_username if available, or null if not found or on error.
   */
  private async fetchUserInfo(): Promise<string | null> {
    try {
      const response: any = await firstValueFrom(this.http.get(authConfig.userinfoEndpoint));

      // Store preferred_username
      if (response?.preferred_username) {
        return response.preferred_username;
      }
      this.logging.logEvent(QfaEventType.INFO, `AuthService.fetchUserInfo - received response: ${response}`);
      return null; // If preferred_username not present
    } catch (error) {
      this.logging.logEvent(QfaEventType.WARNING, 'Failed to fetch user info.');
      this.logging.logError(error as Error);
      return null;
    }
  }

  private async _storeSFObjectsLocally(tokenResponse: OAuthAuthorizationResponse): Promise<string | null> {
    sessionStorage.setItem('sf_access_token', tokenResponse.access_token);
    const username = await this.fetchUserInfo();
    sessionStorage.setItem('username', username ?? '');
    if (tokenResponse.refresh_token) {
      sessionStorage.setItem('sf_refresh_token', tokenResponse.refresh_token);
    }
    return Promise.resolve(sessionStorage.getItem('originalUrl'));
  }

  private clearTokens(): void {
    this.logging.logEvent(QfaEventType.DEBUG, 'Clearing Session');
    sessionStorage.removeItem('sf_code_verifier');
    sessionStorage.removeItem('sf_code_challenge_method');
    sessionStorage.removeItem('sf_id_token');
    sessionStorage.removeItem('sf_access_token');
    this.accessJWT = null;
    this.idJWT = null;
  }

  /**
   * Helper method from from attemptTokenExchange() called from call-back screen after salesforce redirects to it after login
   * Encode an object's key/value pairs into X-WWW-Form-URLEncoded string.
   *
   * @returns string - The urlencoded string
   */
  private objectToXWWWFormUrlencoded(value: any): string {
    const out: string[] = [];
    for (const i in value) {
      out.push(encodeURIComponent(i).trim() + '=' + encodeURIComponent(value[i]).trim());
    }
    return out.join('&');
  }

  private captureOriginalUrl() {
    // Retrieve original URL path
    let originalUrl = window.location.href;
    if (
      originalUrl.startsWith(environment.baseUrl) &&
      (originalUrl.includes('/coverage?policyNumber=') || originalUrl.includes('/dashboard?policyNumber='))
    ) {
      originalUrl = originalUrl.replace(environment.baseUrl, '');
      sessionStorage.setItem('originalUrl', originalUrl);
      this.setIFA(originalUrl);
    }
  }

  private setIFA(url: string) {
    if (sessionStorage.getItem('isInternal') === null) {
      this.isInternal = url.includes('user=IA');
      sessionStorage.setItem('isInternal', this.isInternal.toString());
    } else {
      this.isInternal = sessionStorage.getItem('isInternal') === 'true';
    }
  }

  /**
   * Return true if an access token exists
   *
   * @returns boolean - true if access token exists
   */
  isLoggedIn(): boolean {
    if (sessionStorage.getItem('sf_access_token') === null) {
      return false;
    }
    return true;
  }
}

/**
 * The config type for AuthService
 */
export type AuthServiceConfig = {
  issuer: string;
  clientId: string;
  scope: string;
  redirectUri: string;
  loginUrl: string;
  introspectionEndpoint: string;
  revocationEndpoint: string;
  tokenEndpoint: string;
  userinfoEndpoint: string;
  logoutUrl: string;
  salesForcePKCEGeneratorUrl: string;
};

/**
 * The OAuth authorization repsonse type
 */
export type OAuthAuthorizationResponse = {
  access_token: string;
  id: string;
  id_token: string;
  instance_url: string;
  issued_at: string;
  scope: string;
  signature: string;
  token_format: string;
  token_type: string;
  refresh_token?: string;
};

export type SalesForcePKCEGeneratorResponse = {
  code_challenge: string;
  code_verifier: string;
  code_challenge_method: string;
};

export const authConfig: AuthServiceConfig = {
  issuer: environment.oAuthBaseUrl,
  clientId: environment.oAuthClientId,
  scope: 'openid api refresh_token',

  redirectUri: environment.oAuthCallBackUrl,

  loginUrl: environment.oAuthBaseUrl + '/services/oauth2/authorize',
  revocationEndpoint: environment.oAuthBaseUrl + '/services/oauth2/revoke',
  introspectionEndpoint: environment.oAuthBaseUrl + '/services/oauth2/introspect',
  tokenEndpoint: environment.oAuthBaseUrl + '/services/oauth2/token',
  userinfoEndpoint: environment.oAuthBaseUrl + '/services/oauth2/userinfo',
  logoutUrl: environment.oAuthBaseUrl + '/services/oauth2/logout',

  salesForcePKCEGeneratorUrl: environment.oAuthBaseUrl + '/services/oauth2/pkce/generator',
};
